import { RouteObject, Navigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import AppContainer from "./components/app/AppContainer";
import ProfilePage from "./components/pages/profile";
import AdminDashboardPage from "./components/pages/admin-dashboard";
import ProtectedRoute from "./routesWrappers/ProtectedRoute";
import { roleHomePath } from "./utils/role-home";
import SuspenseWrapper from "./routesWrappers/SuspenseWrapper";
import RouteError from "./components/common/route-error";
import OAuthCallback from "./components/authorization/OAuthCallback";
import NotFoundPage from "./components/pages/not-found/NotFoundPage";
import AuthRequiredLayout from "./components/layouts/AuthRequiredLayout";
import AdaptiveLayout from "./components/layouts/AdaptiveLayout";
import AuthPage from "./components/authorization/AuthPage";
import { lazyWithRetry } from "./utils/lazy-with-retry";
import type { RootState } from "./store";

// Lazy-loaded pages
const LazyUsersPage = lazyWithRetry(
  () => import("./components/pages/admin/pages/students-page")
);
const LazyCuratorStudentsPage = lazyWithRetry(
  () => import("./components/pages/admin/pages/students-page/CuratorStudentsPage")
);
const LazyCuratorDashboardPage = lazyWithRetry(
  () => import("./components/pages/curator-dashboard")
);
const LazyUserDetailsPage = lazyWithRetry(
  () => import("./components/pages/admin/pages/user-details-page")
);
const LazyExerciseConstructorPage = lazyWithRetry(
  () => import("./components/pages/exercises-constructor")
);
const LazyExerciseGeneratorPage = lazyWithRetry(
  () => import("./components/pages/exercise-generator")
);
const LazyUserProgressPage = lazyWithRetry(
  () => import("./components/pages/admin/pages/user-progress-page")
);
const LazyUserExerciseViewPage = lazyWithRetry(
  () => import("./components/pages/admin/pages/user-exercise-view-page")
);
const LazyCatalogExercisePage = lazyWithRetry(
  () => import("./components/pages/catalog-exercise/pages")
);

const LazyDatenschutzPage = lazyWithRetry(
  () => import("./components/pages/legal/DatenschutzPage")
);
const LazyAgbPage = lazyWithRetry(
  () => import("./components/pages/legal/AgbPage")
);
const LazyImpressumPage = lazyWithRetry(
  () => import("./components/pages/legal/ImpressumPage")
);

const LazyCoursesPageNew = lazyWithRetry(() => import("./components/pages/courses-page"));
const LazyHomePage = lazyWithRetry(() => import("./components/pages/home-page"));
const LazyAdminCoursesPage = lazyWithRetry(() => import("./components/pages/admin-courses-page"));
const LazyCoursePageForAdmin = lazyWithRetry(() => import("./components/pages/course"));
const LazyModulePageForAdmin = lazyWithRetry(
  () => import("./components/pages/module/pages")
);
const LazyExercisePageForAdmin = lazyWithRetry(
  () => import("./components/pages/exercise")
);

const LazyCoursePage = lazyWithRetry(
  () => import("./components/pages/learner/pages/course-page")
);
const LazyModulePage = lazyWithRetry(
  () => import("./components/pages/learner/pages/module-page")
);
const LazyExercisePage = lazyWithRetry(
  () => import("./components/pages/learner/pages/exercise-page")
);

const LazyPlatformPage = lazyWithRetry(
  () => import("./components/pages/learner/pages/platform-page")
);
const LazyPayCoursePage = lazyWithRetry(
  () => import("./components/pages/learner/pages/pay-course-page")
);
const LazySchedulePage = lazyWithRetry(
  () => import("./components/pages/schedule-page")
);
const LazyActivityPage = lazyWithRetry(
  () => import("./components/pages/activity-page")
);
const LazyExercisesBrowsePage = lazyWithRetry(
  () => import("./components/pages/exercises-browse")
);
const LazyVocabReviewPage = lazyWithRetry(
  () => import("./components/pages/learner/pages/vocab-review/vocab-review-page")
);
const LazyProfileDashboard = lazyWithRetry(
  () => import("./components/pages/profile-dashboard")
);
const LazyTransactionsPage = lazyWithRetry(
  () => import("./components/pages/transactions")
);
const LazyMessagesPage = lazyWithRetry(
  () => import("./components/pages/messages")
);
const LazyLogsPage = lazyWithRetry(
  () => import("./components/pages/admin/pages/logs-page")
);
const LazyTranslationCachePage = lazyWithRetry(
  () => import("./components/pages/admin/pages/translation-cache-page")
);
const LazyAiCostsPage = lazyWithRetry(
  () => import("./components/pages/admin/pages/ai-costs-page")
);
const LazyBugReportsPage = lazyWithRetry(
  () => import("./components/pages/admin/pages/bug-reports-page")
);
const LazyReviewsPage = lazyWithRetry(
  () => import("./components/pages/admin/pages/reviews-page")
);

const routes: RouteObject[] = [
  {
    path: "/",
    element: <AppContainer />,
    errorElement: <RouteError />,
    children: [
      // OAuth callback (no layout)
      {
        path: "auth/callback",
        element: <OAuthCallback />,
      },
      // Auth pages (no layout)
      {
        path: "login",
        element: <AuthPage mode="signin" />,
      },
      {
        path: "signup",
        element: <AuthPage mode="signup" />,
      },
      {
        path: "accept-invite",
        element: <AuthPage mode="signin" />,
      },

      // ========================================
      // Content routes (DashboardLayout if auth'd, PublicLayout if anon)
      // ========================================
      {
        element: <AdaptiveLayout />,
        errorElement: <RouteError />,
        children: [
          {
            index: true,
            element: <PublicHome />,
          },
          {
            path: "courses",
            children: [
              {
                index: true,
                element: (
                  <SuspenseWrapper>
                    <LazyCoursesPageNew />
                  </SuspenseWrapper>
                ),
              },
              {
                path: ":courseSlug",
                children: [
                  {
                    index: true,
                    element: (
                      <SuspenseWrapper>
                        <LazyCoursePage />
                      </SuspenseWrapper>
                    ),
                  },
                  {
                    path: ":moduleSlug",
                    children: [
                      {
                        index: true,
                        element: (
                          <SuspenseWrapper>
                            <LazyModulePage />
                          </SuspenseWrapper>
                        ),
                      },
                      {
                        path: ":exerciseSlug",
                        element: (
                          <SuspenseWrapper>
                            <LazyExercisePage />
                          </SuspenseWrapper>
                        ),
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            path: "course/:courseSlug",
            children: [
              {
                index: true,
                element: <OldCourseRedirect />,
              },
              {
                path: "module/:moduleSlug",
                children: [
                  {
                    index: true,
                    element: <OldModuleRedirect />,
                  },
                  {
                    path: "exercise/:exerciseSlug",
                    element: <OldExerciseRedirect />,
                  },
                ],
              },
            ],
          },
          {
            path: "exercises",
            element: (
              <SuspenseWrapper>
                <LazyExercisesBrowsePage />
              </SuspenseWrapper>
            ),
          },
          {
            path: "exercises/:exerciseSlug",
            element: (
              <SuspenseWrapper>
                <LazyExercisePage />
              </SuspenseWrapper>
            ),
          },
          {
            path: "catalog-exercise",
            element: (
              <SuspenseWrapper>
                <LazyCatalogExercisePage />
              </SuspenseWrapper>
            ),
          },
          {
            path: "datenschutz",
            element: (
              <SuspenseWrapper>
                <LazyDatenschutzPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: "agb",
            element: (
              <SuspenseWrapper>
                <LazyAgbPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: "impressum",
            element: (
              <SuspenseWrapper>
                <LazyImpressumPage />
              </SuspenseWrapper>
            ),
          },
        ],
      },

      // ========================================
      // Authenticated routes (DashboardLayout — sidebar, top bar)
      // ========================================
      {
        element: <AuthRequiredLayout />,
        errorElement: <RouteError />,
        children: [
          {
            path: "dashboard",
            element: (
              <SuspenseWrapper>
                <LazyActivityPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: "profile",
            element: (
              <SuspenseWrapper>
                <LazyProfileDashboard />
              </SuspenseWrapper>
            ),
          },
          {
            path: "settings",
            element: <ProfilePage />,
          },
          {
            path: "schedule",
            element: (
              <SuspenseWrapper>
                <LazySchedulePage />
              </SuspenseWrapper>
            ),
          },
          {
            path: "activity",
            element: <Navigate to="/dashboard" replace />,
          },
          {
            path: "vocabulary",
            element: (
              <SuspenseWrapper>
                <LazyVocabReviewPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: "messages",
            element: (
              <SuspenseWrapper>
                <LazyMessagesPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: "messages/:conversationId",
            element: (
              <SuspenseWrapper>
                <LazyMessagesPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: "learner",
            children: [
              {
                index: true,
                element: (
                  <SuspenseWrapper>
                    <LazyPlatformPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: "pay",
                element: (
                  <SuspenseWrapper>
                    <LazyPayCoursePage />
                  </SuspenseWrapper>
                ),
              },
            ],
          },

          // Admin routes
          {
            path: "admin",
            children: [
              { index: true, element: <ProtectedRoute requiredRole="ADMIN"><AdminDashboardPage /></ProtectedRoute> },
              { path: "users", element: <ProtectedRoute requiredRole="ADMIN"><SuspenseWrapper><LazyUsersPage /></SuspenseWrapper></ProtectedRoute> },
              { path: "users/:userId", element: <ProtectedRoute requiredRole="ADMIN"><SuspenseWrapper><LazyUserDetailsPage /></SuspenseWrapper></ProtectedRoute> },
              { path: "users/:userId/progress", element: <ProtectedRoute requiredRole="ADMIN"><SuspenseWrapper><LazyUserProgressPage /></SuspenseWrapper></ProtectedRoute> },
              { path: "users/:userId/progress/exercise/:exerciseId", element: <ProtectedRoute requiredRole="ADMIN"><SuspenseWrapper><LazyUserExerciseViewPage /></SuspenseWrapper></ProtectedRoute> },
              { path: "courses", element: <ProtectedRoute requiredRole="ADMIN"><SuspenseWrapper><LazyAdminCoursesPage /></SuspenseWrapper></ProtectedRoute> },
              { path: "transactions", element: <ProtectedRoute requiredRole="ADMIN"><SuspenseWrapper><LazyTransactionsPage /></SuspenseWrapper></ProtectedRoute> },
              { path: "messages", element: <ProtectedRoute requiredRole="ADMIN"><SuspenseWrapper><LazyMessagesPage /></SuspenseWrapper></ProtectedRoute> },
              { path: "messages/:conversationId", element: <ProtectedRoute requiredRole="ADMIN"><SuspenseWrapper><LazyMessagesPage /></SuspenseWrapper></ProtectedRoute> },
              { path: "logs", element: <ProtectedRoute requiredRole="ADMIN"><SuspenseWrapper><LazyLogsPage /></SuspenseWrapper></ProtectedRoute> },
              { path: "bug-reports", element: <ProtectedRoute requiredRole="ADMIN"><SuspenseWrapper><LazyBugReportsPage /></SuspenseWrapper></ProtectedRoute> },
              { path: "reviews", element: <ProtectedRoute requiredRole="ADMIN"><SuspenseWrapper><LazyReviewsPage /></SuspenseWrapper></ProtectedRoute> },
              { path: "profile", element: <ProtectedRoute requiredRole="ADMIN"><SuspenseWrapper><LazyProfileDashboard /></SuspenseWrapper></ProtectedRoute> },
              { path: "settings", element: <ProtectedRoute requiredRole="ADMIN"><ProfilePage /></ProtectedRoute> },
              { path: "vocabulary", element: <ProtectedRoute requiredRole="ADMIN"><SuspenseWrapper><LazyVocabReviewPage /></SuspenseWrapper></ProtectedRoute> },
              { path: "translations", element: <ProtectedRoute requiredRole="ADMIN"><SuspenseWrapper><LazyTranslationCachePage /></SuspenseWrapper></ProtectedRoute> },
              { path: "ai-costs", element: <ProtectedRoute requiredRole="ADMIN"><SuspenseWrapper><LazyAiCostsPage /></SuspenseWrapper></ProtectedRoute> },
              { path: "exercises-constructor", element: <ProtectedRoute requiredRole="ADMIN"><SuspenseWrapper><LazyExerciseConstructorPage /></SuspenseWrapper></ProtectedRoute> },
              { path: "exercises-constructor/:exerciseId", element: <ProtectedRoute requiredRole="ADMIN"><SuspenseWrapper><LazyExerciseConstructorPage /></SuspenseWrapper></ProtectedRoute> },
              { path: "exercises-constructor/:courseSlug/:moduleSlug/:exerciseSlug", element: <ProtectedRoute requiredRole="ADMIN"><SuspenseWrapper><LazyExerciseConstructorPage /></SuspenseWrapper></ProtectedRoute> },
              { path: "exercise-generator", element: <ProtectedRoute requiredRole="ADMIN"><SuspenseWrapper><LazyExerciseGeneratorPage /></SuspenseWrapper></ProtectedRoute> },
              {
                path: "course/:courseSlug",
                children: [
                  { index: true, element: <ProtectedRoute requiredRole="ADMIN"><SuspenseWrapper><LazyCoursePageForAdmin /></SuspenseWrapper></ProtectedRoute> },
                  {
                    path: "module/:moduleSlug",
                    children: [
                      { index: true, element: <ProtectedRoute requiredRole="ADMIN"><SuspenseWrapper><LazyModulePageForAdmin /></SuspenseWrapper></ProtectedRoute> },
                      { path: "exercise/:exerciseSlug", element: <ProtectedRoute requiredRole="ADMIN"><SuspenseWrapper><LazyExercisePageForAdmin /></SuspenseWrapper></ProtectedRoute> },
                    ],
                  },
                ],
              },
            ],
          },

          // Curator routes
          {
            path: "curator",
            children: [
              { index: true, element: <ProtectedRoute requiredRole="CURATOR"><SuspenseWrapper><LazyCuratorDashboardPage /></SuspenseWrapper></ProtectedRoute> },
              { path: "users", element: <ProtectedRoute requiredRole="CURATOR"><SuspenseWrapper><LazyCuratorStudentsPage /></SuspenseWrapper></ProtectedRoute> },
              { path: "users/:userId", element: <ProtectedRoute requiredRole="CURATOR"><SuspenseWrapper><LazyUserDetailsPage /></SuspenseWrapper></ProtectedRoute> },
              { path: "users/:userId/progress", element: <ProtectedRoute requiredRole="CURATOR"><SuspenseWrapper><LazyUserProgressPage /></SuspenseWrapper></ProtectedRoute> },
              { path: "users/:userId/progress/exercise/:exerciseId", element: <ProtectedRoute requiredRole="CURATOR"><SuspenseWrapper><LazyUserExerciseViewPage /></SuspenseWrapper></ProtectedRoute> },
              { path: "courses", element: <ProtectedRoute requiredRole="CURATOR"><SuspenseWrapper><LazyCoursesPageNew /></SuspenseWrapper></ProtectedRoute> },
              {
                path: "courses/:courseSlug",
                children: [
                  { index: true, element: <ProtectedRoute requiredRole="CURATOR"><SuspenseWrapper><LazyCoursePage /></SuspenseWrapper></ProtectedRoute> },
                  {
                    path: ":moduleSlug",
                    children: [
                      { index: true, element: <ProtectedRoute requiredRole="CURATOR"><SuspenseWrapper><LazyModulePage /></SuspenseWrapper></ProtectedRoute> },
                      { path: ":exerciseSlug", element: <ProtectedRoute requiredRole="CURATOR"><SuspenseWrapper><LazyExercisePage /></SuspenseWrapper></ProtectedRoute> },
                    ],
                  },
                ],
              },
              { path: "vocabulary", element: <ProtectedRoute requiredRole="CURATOR"><SuspenseWrapper><LazyVocabReviewPage /></SuspenseWrapper></ProtectedRoute> },
              { path: "messages", element: <ProtectedRoute requiredRole="CURATOR"><SuspenseWrapper><LazyMessagesPage /></SuspenseWrapper></ProtectedRoute> },
              { path: "messages/:conversationId", element: <ProtectedRoute requiredRole="CURATOR"><SuspenseWrapper><LazyMessagesPage /></SuspenseWrapper></ProtectedRoute> },
              { path: "profile", element: <ProtectedRoute requiredRole="CURATOR"><SuspenseWrapper><LazyProfileDashboard /></SuspenseWrapper></ProtectedRoute> },
              { path: "settings", element: <ProtectedRoute requiredRole="CURATOR"><ProfilePage /></ProtectedRoute> },
            ],
          },
        ],
      },

      // 404
      {
        path: "*",
        element: <NotFoundPage />,
      },
    ],
  },
];

/**
 * `/` root: authenticated users go to their dashboard; anonymous visitors
 * get the public, indexable marketing home page (rendered in PublicLayout).
 *
 * "Their dashboard" is role-dependent: admins and curators have their own,
 * and login lands here, so sending everyone to /dashboard dropped admins on
 * the learner view. Mirrors the Sidebar logo target, including its rule that
 * an admin who is impersonating is treated as a student.
 */
function PublicHome() {
  const isAuthenticated = useSelector((s: RootState) => s.session.isAuthenticated);
  const role = useSelector((s: RootState) => s.session.user?.role);
  const isImpersonating = useSelector((s: RootState) => s.impersonation.isImpersonating);

  if (isAuthenticated) {
    return <Navigate to={roleHomePath(role, isImpersonating)} replace />;
  }
  return (
    <SuspenseWrapper>
      <LazyHomePage />
    </SuspenseWrapper>
  );
}

/** Redirect legacy /course/:slug → /courses/:slug */
function OldCourseRedirect() {
  const { courseSlug } = useParams();
  return <Navigate to={`/courses/${courseSlug}`} replace />;
}

/** Redirect legacy /course/:slug/module/:mod → /courses/:slug/:mod */
function OldModuleRedirect() {
  const { courseSlug, moduleSlug } = useParams();
  return <Navigate to={`/courses/${courseSlug}/${moduleSlug}`} replace />;
}

/** Redirect legacy /course/:slug/module/:mod/exercise/:ex → /courses/:slug/:mod/:ex */
function OldExerciseRedirect() {
  const { courseSlug, moduleSlug, exerciseSlug } = useParams();
  return <Navigate to={`/courses/${courseSlug}/${moduleSlug}/${exerciseSlug}`} replace />;
}

export default routes;
